import type { ClintplayEvent, NavigationEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;

export class NavigationTracker {
  private currentUrl: string = '';
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;
  private popstateHandler: (() => void) | null = null;
  private hashchangeHandler: (() => void) | null = null;

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  start(): void {
    this.currentUrl = window.location.href;

    // Emit initial navigation event
    this.emitNavigation(null, this.currentUrl, 'initial');

    // Patch history.pushState
    this.originalPushState = history.pushState.bind(history);
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      this.originalPushState!(...args);
      this.handleUrlChange('pushState');
    };

    // Patch history.replaceState
    this.originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      this.originalReplaceState!(...args);
      this.handleUrlChange('replaceState');
    };

    // Listen for popstate (back/forward)
    this.popstateHandler = () => this.handleUrlChange('popstate');
    window.addEventListener('popstate', this.popstateHandler);

    // Listen for hashchange
    this.hashchangeHandler = () => this.handleUrlChange('hashchange');
    window.addEventListener('hashchange', this.hashchangeHandler);
  }

  stop(): void {
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
    if (this.hashchangeHandler) {
      window.removeEventListener('hashchange', this.hashchangeHandler);
      this.hashchangeHandler = null;
    }
  }

  private handleUrlChange(trigger: NavigationEventData['trigger']): void {
    const newUrl = window.location.href;
    if (newUrl !== this.currentUrl) {
      const from = this.currentUrl;
      this.currentUrl = newUrl;
      this.emitNavigation(from, newUrl, trigger);
    }
  }

  private emitNavigation(from: string | null, to: string, trigger: NavigationEventData['trigger']): void {
    const data: NavigationEventData = { from, to, trigger };
    this.emit({
      type: 'navigation',
      timestamp: Date.now(),
      sessionId: this.sessionId,
      data,
    });
  }
}
